import themesRaw from './themes.json';

export type ThemeId = keyof typeof themesRaw;
export type ThemeTokenKey = keyof (typeof themesRaw)[ThemeId];
export type ThemeDefinition = Record<ThemeTokenKey, string>;

const themes: Record<ThemeId, ThemeDefinition> = themesRaw;

export const THEME_ORDER: ThemeId[] = [
  'lightest-1',
  'light-1',
  'light-2',
  'bright-1',
  'bright-2',
  'dark-1',
  'dark-2',
  'darkest-1',
];

const FALLBACK_TEXT = '#121212';
const FALLBACK_TEXT_INVERTED = '#FFFFFF';

function hexToRgb(hex: string): [number, number, number] {
  const normalized = hex.startsWith('#') ? hex.slice(1) : hex;
  if (normalized.length !== 6) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);
  return [r, g, b];
}

function channelToLinear(channel: number): number {
  const ratio = channel / 255;
  return ratio <= 0.03928 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * channelToLinear(r) + 0.7152 * channelToLinear(g) + 0.0722 * channelToLinear(b);
}

function contrastRatio(foreground: string, background: string): number {
  const lumA = relativeLuminance(foreground);
  const lumB = relativeLuminance(background);
  const brightest = Math.max(lumA, lumB);
  const darkest = Math.min(lumA, lumB);
  return (brightest + 0.05) / (darkest + 0.05);
}

function pickAccentForeground(theme: ThemeDefinition): string {
  const accent = theme.accent;
  const candidates = [
    theme['primary-fg'],
    theme.text,
    theme.heading,
    FALLBACK_TEXT,
    FALLBACK_TEXT_INVERTED,
  ];

  let bestColor = candidates[0];
  let bestContrast = contrastRatio(bestColor, accent);

  for (const candidate of candidates.slice(1)) {
    const ratio = contrastRatio(candidate, accent);
    if (ratio > bestContrast) {
      bestContrast = ratio;
      bestColor = candidate;
    }
  }

  if (bestContrast < 4.5) {
    // Ensure WCAG minimum by falling back to whichever of the defaults is better.
    const fallbackContrast = contrastRatio(FALLBACK_TEXT, accent);
    const invertedContrast = contrastRatio(FALLBACK_TEXT_INVERTED, accent);
    bestColor = fallbackContrast >= invertedContrast ? FALLBACK_TEXT : FALLBACK_TEXT_INVERTED;
  }

  return bestColor;
}

export function getThemeDefinition(id: ThemeId): ThemeDefinition {
  return themes[id];
}

export function applyTheme(el: HTMLElement, id: ThemeId) {
  const theme = themes[id];
  if (!theme) {
    throw new Error(`Unknown theme: ${id}`);
  }

  el.setAttribute('data-theme', id);

  Object.entries(theme).forEach(([token, value]) => {
    el.style.setProperty(`--${token}`, value);
  });

  // Derived aliases used across the design system.
  el.style.setProperty('--background', theme.bg);
  el.style.setProperty('--foreground', theme.text);
  el.style.setProperty('--card', theme.surface);
  el.style.setProperty('--card-foreground', theme.text);
  el.style.setProperty('--popover', theme.surface);
  el.style.setProperty('--popover-foreground', theme.text);
  el.style.setProperty('--secondary', theme['surface-alt']);
  el.style.setProperty('--secondary-foreground', theme.text);
  el.style.setProperty('--primary-foreground', theme['primary-fg']);
  el.style.setProperty('--muted-foreground', theme['text-muted']);
  el.style.setProperty('--input-background', theme.surface);
  el.style.setProperty('--switch-background', theme.border);
  el.style.setProperty('--sidebar', theme.surface);
  el.style.setProperty('--sidebar-foreground', theme.text);
  el.style.setProperty('--sidebar-primary', theme.primary);
  el.style.setProperty('--sidebar-primary-foreground', theme['primary-fg']);
  el.style.setProperty('--sidebar-accent', theme.accent);
  el.style.setProperty('--sidebar-border', theme.border);
  el.style.setProperty('--sidebar-ring', theme.ring);
  el.style.setProperty('--tag-background', theme.tag);
  el.style.setProperty('--chart-1', theme.primary);
  el.style.setProperty('--chart-2', theme.accent);
  el.style.setProperty('--chart-3', theme.muted);
  el.style.setProperty('--chart-4', theme.link);
  el.style.setProperty('--chart-5', theme['text-muted']);

  const accentForeground = pickAccentForeground(theme);
  el.style.setProperty('--accent-foreground', accentForeground);
  el.style.setProperty('--destructive', theme.accent);
  el.style.setProperty('--destructive-foreground', accentForeground);
  el.style.setProperty('--sidebar-accent-foreground', accentForeground);
}

export function getThemeCssSnippet(id: ThemeId): string {
  const theme = themes[id];
  const body = Object.entries(theme)
    .map(([token, value]) => `  --${token}: ${value};`)
    .join('\n');
  return `:root[data-theme="${id}"] {\n${body}\n}`;
}

export function listThemes(): Array<{ id: ThemeId; definition: ThemeDefinition }> {
  return THEME_ORDER.map((id) => ({ id, definition: themes[id] }));
}
